import {
  PieChart as MuiPieChart,
  type PieChartProps as MuiPieChartProps,
} from "@mui/x-charts/PieChart";
import { useTheme } from "@mui/material/styles";
import { useChartColors } from "./useChartColors";

export interface PieChartProps extends Omit<MuiPieChartProps, "series"> {
  series?: MuiPieChartProps["series"];
}

export function PieChart({ series, colors, ...props }: PieChartProps) {
  const theme = useTheme();
  const chart = theme.palette.chart;
  const sliceCount = series?.[0]?.data?.length ?? 0;
  const seriesColors = useChartColors(sliceCount);

  // Set default highlight scope for cool hover fading effects
  const modifiedSeries = series?.map((s) => ({
    ...s,
    highlightScope: s.highlightScope ?? { highlighted: "item", faded: "global" },
  }));

  return (
    <MuiPieChart
      series={modifiedSeries ?? []}
      colors={colors ?? seriesColors}
      slotProps={{
        legend: {
          itemMarkWidth: 12,
          itemMarkHeight: 12,
          padding: { bottom: 20 },
        },
      }}
      sx={{
        "& .MuiChartsLegend-series text": {
          fill: `${chart.legendFg} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: "14px !important",
        },
        "& .MuiChartsLegend-mark": {
          ry: 4,
          rx: 4,
          width: 12,
          height: 12,
        },
        "& .MuiChartsTooltip-root": {
          fontFamily: theme.typography.fontFamily,
          fontSize: typeof theme.typography.body1.fontSize === "number" ? theme.typography.body1.fontSize : 14,
        },
        "& .MuiChartsTooltip-table": {
          backgroundColor: chart.tooltipBg,
          color: chart.tooltipFg,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[4],
        },
        ...((props.sx as object) ?? {}),
      }}
      {...props}
    />
  );
}
